import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Bell, 
  BellOff, 
  Settings, 
  Plus,
  Play,
  Pause,
  Eye,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { useAlerting, useBrowserNotifications } from '@/hooks/alerts/useAlerting';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export const AlertsDashboard: React.FC = () => {
  const {
    alertRules,
    activeAlerts,
    recentAlerts,
    notificationChannels,
    alertStats,
    loading,
    createAlertRule,
    toggleAlertRule,
    acknowledgeAlert,
    resolveAlert,
    createNotificationChannel,
    processAlerts,
    refreshData
  } = useAlerting();

  const {
    supported: notificationSupported,
    permission: notificationPermission,
    requestPermission,
    showAlertNotification
  } = useBrowserNotifications();

  // Show browser notifications for new alerts
  useEffect(() => {
    if (activeAlerts.length > 0 && notificationPermission === 'granted') {
      const latestAlert = activeAlerts[0];
      const rule = alertRules.find(r => r.id === latestAlert.alert_rule_id);
      
      // Only show notification for very recent alerts (last 2 minutes)
      const alertTime = new Date(latestAlert.triggered_at).getTime();
      const now = Date.now();
      if (now - alertTime < 2 * 60 * 1000) {
        showAlertNotification(latestAlert, rule);
      }
    }
  }, [activeAlerts, alertRules, notificationPermission, showAlertNotification]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-destructive text-destructive-foreground';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="h-4 w-4" />;
      case 'high': return <AlertTriangle className="h-4 w-4" />;
      case 'medium': return <Clock className="h-4 w-4" />;
      case 'low': return <Eye className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const handleProcessAlerts = async () => {
    const success = await processAlerts();
    if (success) {
      toast.success('Alert processing triggered successfully');
    } else {
      toast.error('Failed to trigger alert processing');
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    const success = await acknowledgeAlert(alertId);
    if (success) {
      toast.success('Alert acknowledged');
    } else {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    const success = await resolveAlert(alertId);
    if (success) {
      toast.success('Alert resolved');
    } else {
      toast.error('Failed to resolve alert');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Real-time Alerts</h1>
          <p className="text-muted-foreground">Monitor and manage system alerts</p>
        </div>
        <div className="flex gap-2">
          {notificationSupported && notificationPermission !== 'granted' && (
            <Button variant="outline" onClick={requestPermission}>
              <Bell className="h-4 w-4 mr-2" />
              Enable Notifications
            </Button>
          )}
          <Button variant="outline" onClick={handleProcessAlerts}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Process Alerts
          </Button>
          <CreateAlertRuleDialog onRuleCreated={refreshData} notificationChannels={notificationChannels} />
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktive varsler</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{alertStats.active_alerts}</div>
            <p className="text-xs text-muted-foreground">Krever oppmerksomhet</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Kritiske varsler</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{alertStats.critical_alerts}</div>
            <p className="text-xs text-muted-foreground">Høy prioritet</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Løste varsler</CardTitle>
            <CheckCircle className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{alertStats.resolved_alerts}</div>
            <p className="text-xs text-muted-foreground">Siste 7 dager</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Varslingsregler</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{alertRules.length}</div>
            <p className="text-xs text-muted-foreground">
              {alertRules.filter(r => r.is_active).length} aktive
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Aktive varsler</TabsTrigger>
          <TabsTrigger value="rules">Varslingsregler</TabsTrigger>
          <TabsTrigger value="channels">Varslingskanaler</TabsTrigger>
          <TabsTrigger value="analytics">Analyse</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {activeAlerts.length > 0 ? (
            <div className="space-y-4">
              {activeAlerts.map((alert) => {
                const rule = alertRules.find(r => r.id === alert.alert_rule_id);
                return (
                  <Card key={alert.id} className="border-l-4 border-l-destructive">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getSeverityIcon(rule?.severity || 'medium')}
                          <div>
                            <CardTitle className="text-lg">
                              {rule?.name || 'Unknown Rule'}
                            </CardTitle>
                            <CardDescription>{alert.message}</CardDescription>
                          </div>
                          <Badge className={getSeverityColor(rule?.severity || 'medium')}>
                            {rule?.severity || 'medium'}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleAcknowledgeAlert(alert.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Bekreft
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleResolveAlert(alert.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Løs
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <strong>Utløst:</strong> {new Date(alert.triggered_at).toLocaleString('no-NO')}
                        </div>
                        <div>
                          <strong>Verdi:</strong> {alert.trigger_value}
                        </div>
                        <div>
                          <strong>Status:</strong> {alert.status}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Ingen aktive varsler</h3>
                <p className="text-muted-foreground">Alle systemer fungerer normalt.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rules" className="space-y-4">
          {alertRules.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Ingen varslingsregler funnet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Opprett din første varslingsregel for å overvåke systemet automatisk.
              </p>
              <CreateAlertRuleDialog onRuleCreated={refreshData} notificationChannels={notificationChannels}>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Opprett første varslingsregel
                </Button>
              </CreateAlertRuleDialog>
            </div>
          ) : (
          <div className="space-y-4">
            {alertRules.map((rule) => (
              <Card key={rule.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {rule.name}
                        <Badge className={getSeverityColor(rule.severity)}>
                          {rule.severity}
                        </Badge>
                        {rule.is_active ? (
                          <Badge variant="secondary">Aktiv</Badge>
                        ) : (
                          <Badge variant="outline">Inaktiv</Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{rule.description}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={(checked) => toggleAlertRule(rule.id, checked)}
                      />
                      <Button variant="outline" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <strong>Type:</strong> {rule.rule_type}
                    </div>
                    <div>
                      <strong>Terskel:</strong> {rule.threshold_operator} {rule.threshold_value}
                    </div>
                    <div>
                      <strong>Tidsvindu:</strong> {rule.time_window_minutes} min
                    </div>
                    <div>
                      <strong>Kanaler:</strong> {rule.notification_channels.length}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </TabsContent>

        <TabsContent value="channels" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Varslingskanaler</h3>
            <CreateChannelDialog onChannelCreated={refreshData} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {notificationChannels.map((channel) => (
              <Card key={channel.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{channel.name}</CardTitle>
                    <Badge variant={channel.is_active ? "secondary" : "outline"}>
                      {channel.is_active ? "Aktiv" : "Inaktiv"}
                    </Badge>
                  </div>
                  <CardDescription>Type: {channel.type}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Opprettet: {new Date(channel.created_at).toLocaleDateString('no-NO')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Varselstrend</CardTitle>
              <CardDescription>Daglige varsler siste uke</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={alertStats.alert_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="hsl(var(--primary))" 
                    fill="hsl(var(--primary))" 
                    fillOpacity={0.6} 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Dialog for creating alert rules
const CreateAlertRuleDialog: React.FC<{ 
  onRuleCreated: () => void; 
  notificationChannels: any[];
  children?: React.ReactNode;
}> = ({ 
  onRuleCreated, 
  notificationChannels,
  children
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_type: 'error_rate',
    threshold_value: 10,
    threshold_operator: 'gt',
    time_window_minutes: 5,
    severity: 'medium',
    notification_channels: [] as string[]
  });

  const { createAlertRule } = useAlerting();

  const handleSubmit = async () => {
    if (!formData.name) return;

    const success = await createAlertRule({
      ...formData,
      conditions: {} // Add conditions based on rule_type
    });

    if (success) {
      setOpen(false);
      setFormData({
        name: '',
        description: '',
        rule_type: 'error_rate',
        threshold_value: 10,
        threshold_operator: 'gt',
        time_window_minutes: 5,
        severity: 'medium',
        notification_channels: []
      });
      onRuleCreated();
      toast.success('Alert rule created successfully');
    } else {
      toast.error('Failed to create alert rule');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Ny varslingsregel
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Opprett varslingsregel</DialogTitle>
          <DialogDescription>
            Definer en ny regel for automatisk varsling
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Navn</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="f.eks. Høy feilrate"
              />
            </div>
            <div>
              <Label htmlFor="rule_type">Type</Label>
              <Select value={formData.rule_type} onValueChange={(value) => setFormData(prev => ({ ...prev, rule_type: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="error_rate">Feilrate</SelectItem>
                  <SelectItem value="performance">Ytelse</SelectItem>
                  <SelectItem value="user_activity">Brukeraktivitet</SelectItem>
                  <SelectItem value="ab_test">A/B Test</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Beskrivelse av varslingsregelen..."
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="threshold_operator">Operator</Label>
              <Select value={formData.threshold_operator} onValueChange={(value) => setFormData(prev => ({ ...prev, threshold_operator: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gt">Større enn</SelectItem>
                  <SelectItem value="gte">Større enn eller lik</SelectItem>
                  <SelectItem value="lt">Mindre enn</SelectItem>
                  <SelectItem value="lte">Mindre enn eller lik</SelectItem>
                  <SelectItem value="eq">Lik</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="threshold_value">Terskelverdi</Label>
              <Input
                id="threshold_value"
                type="number"
                value={formData.threshold_value}
                onChange={(e) => setFormData(prev => ({ ...prev, threshold_value: parseInt(e.target.value) }))}
              />
            </div>
            <div>
              <Label htmlFor="time_window">Tidsvindu (min)</Label>
              <Input
                id="time_window"
                type="number"
                value={formData.time_window_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, time_window_minutes: parseInt(e.target.value) }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="severity">Alvorlighetsgrad</Label>
            <Select value={formData.severity} onValueChange={(value) => setFormData(prev => ({ ...prev, severity: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Lav</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">Høy</SelectItem>
                <SelectItem value="critical">Kritisk</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleSubmit} className="w-full">
            Opprett varslingsregel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for creating notification channels
const CreateChannelDialog: React.FC<{ onChannelCreated: () => void }> = ({ onChannelCreated }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'browser',
    config: {}
  });

  const { createNotificationChannel } = useAlerting();

  const handleSubmit = async () => {
    if (!formData.name) return;

    const success = await createNotificationChannel(formData);
    if (success) {
      setOpen(false);
      setFormData({ name: '', type: 'browser', config: {} });
      onChannelCreated();
      toast.success('Notification channel created');
    } else {
      toast.error('Failed to create channel');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Ny kanal
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Opprett varslingskanal</DialogTitle>
          <DialogDescription>
            Legg til en ny kanal for varsler
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="channel_name">Navn</Label>
            <Input
              id="channel_name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="f.eks. Slack Development Channel"
            />
          </div>
          <div>
            <Label htmlFor="channel_type">Type</Label>
            <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="browser">Nettleser</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
                <SelectItem value="email">E-post (kommer snart)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSubmit} className="w-full">
            Opprett kanal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};